import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Palette, Sun, Moon, Loader2, SlidersHorizontal, MonitorSmartphone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useColorTheme, themeMeta } from "@/hooks/useColorTheme";

/** Miniature app mock-up painted from the theme's swatches — shows the palette before you pick it. */
const ThemeSwatchPreview = ({ swatch, tone }) => (
  <div
    className="relative h-28 w-full overflow-hidden rounded-t-[11px]"
    style={{ background: swatch.bg }}
    aria-hidden="true"
  >
    {/* rail */}
    <div className="absolute inset-y-0 left-0 w-7" style={{ background: swatch.surface, borderRight: `1px solid ${swatch.border}` }}>
      <div className="mx-auto mt-2 h-3.5 w-3.5 rounded" style={{ background: swatch.primary }} />
      <div className="mx-auto mt-2 h-2 w-3 rounded-sm" style={{ background: swatch.border }} />
      <div className="mx-auto mt-1.5 h-2 w-3 rounded-sm" style={{ background: swatch.border }} />
      <div className="mx-auto mt-1.5 h-2 w-3 rounded-sm" style={{ background: swatch.border }} />
    </div>
    {/* page */}
    <div className="absolute inset-y-0 left-7 right-0 p-2.5">
      <div className="flex items-center justify-between">
        <div className="h-2.5 w-16 rounded-sm" style={{ background: swatch.text }} />
        <div className="h-4 w-12 rounded-full" style={{ background: swatch.primary }} />
      </div>
      <div className="mt-2 h-1.5 w-24 rounded-sm" style={{ background: swatch.muted, opacity: 0.8 }} />
      <div
        className="mt-2.5 rounded-md p-2"
        style={{ background: swatch.surface, border: `1px solid ${swatch.border}` }}
      >
        <div className="flex items-center justify-between">
          <div className="h-1.5 w-14 rounded-sm" style={{ background: swatch.muted }} />
          <span className="font-mono-nums text-[10px] font-semibold leading-none" style={{ color: swatch.text }}>
            $128,400
          </span>
        </div>
        <div className="mt-1.5 flex items-center gap-1">
          <div className="h-1.5 flex-1 rounded-sm" style={{ background: swatch.border }} />
          <div className="h-1.5 w-8 rounded-sm" style={{ background: swatch.primary }} />
        </div>
      </div>
    </div>
    <span
      className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full"
      style={{ background: swatch.surface, color: swatch.muted, border: `1px solid ${swatch.border}` }}
    >
      {tone === "light" ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
    </span>
  </div>
);

const SwatchDots = ({ swatch }) => (
  <div className="flex items-center gap-1" aria-label="Palette">
    {[
      ["Background", swatch.bg],
      ["Surface", swatch.surface],
      ["Text", swatch.text],
      ["Bronze", swatch.primary],
      ["Border", swatch.border],
    ].map(([label, color]) => (
      <span
        key={label}
        title={`${label} ${color}`}
        className="h-3.5 w-3.5 rounded-full border border-black/10 shadow-sm"
        style={{ background: color }}
      />
    ))}
  </div>
);

/**
 * "Follow device" — Dark when the phone / laptop is in dark mode, the chosen
 * light theme when it isn't. Saved to the account like every other theme choice.
 */
const FollowDeviceRow = ({ isAuto, systemDark, lightTheme, saving, onToggle }) => {
  const dayName = themeMeta(lightTheme).name;
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border bg-card px-3.5 py-3",
        isAuto ? "border-primary/60 ring-1 ring-primary/40" : "border-border"
      )}
      data-testid="theme-follow-device-row"
      data-active={isAuto || undefined}
    >
      <span
        className={cn(
          "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border",
          isAuto ? "border-primary/40 bg-accent text-primary" : "border-border bg-muted text-muted-foreground"
        )}
        aria-hidden="true"
      >
        <MonitorSmartphone className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <label htmlFor="theme-follow-device" className="flex cursor-pointer items-center gap-1.5 text-sm font-medium">
          Follow device
          {isAuto && (
            <Badge
              variant="outline"
              className="rounded-full border-primary/40 bg-accent px-1.5 py-0 text-[10px] font-medium text-accent-foreground"
              data-testid="theme-follow-device-badge"
            >
              On
            </Badge>
          )}
        </label>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground" data-testid="theme-follow-device-hint">
          {isAuto ? (
            <>
              <span className="font-medium text-foreground">Dark</span> when your device is in dark mode,{" "}
              <span className="font-medium text-foreground">{dayName}</span> when it's in light mode.{" "}
              <span data-testid="theme-device-state">
                Your device is in <span className="font-medium text-foreground">{systemDark ? "dark" : "light"}</span> mode right
                now.
              </span>
            </>
          ) : (
            <>Switch to Dark at night and your light theme by day, automatically, using your phone or computer's setting.</>
          )}
        </p>
      </div>
      {saving ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
      ) : (
        <Switch
          id="theme-follow-device"
          checked={isAuto}
          onCheckedChange={onToggle}
          aria-label="Follow device theme"
          data-testid="theme-follow-device-switch"
        />
      )}
    </div>
  );
};

/**
 * Visual colour-theme picker. Applies instantly on this device and saves to the
 * signed-in user's account so the choice follows them to every other device.
 * With Follow-device on, the light cards choose the daytime theme and Dark is
 * used automatically at night.
 */
export const ThemePicker = ({ compact = false, onPicked }) => {
  const { theme, manualTheme, mode, isAuto, lightTheme, systemDark, setTheme, setMode, setLightTheme, themes } =
    useColorTheme();
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(null); // theme key or "mode"

  const persist = async (payload, successMsg, description) => {
    if (!user) {
      toast.success(successMsg, { id: "color-theme", description });
      return true;
    }
    try {
      await api.put("/auth/me/theme", payload);
      updateUser(payload);
      toast.success(successMsg, {
        id: "color-theme",
        description: description || "Saved to your account — it follows you on desktop and mobile.",
      });
      return true;
    } catch (e) {
      toast.error(`Applied here, but couldn't save to your account: ${errMsg(e)}`, { id: "color-theme" });
      return false;
    }
  };

  const pick = async (t) => {
    if (saving) return;
    if (isAuto) {
      if (t.tone !== "light") {
        toast.info("Dark is used automatically while your device is in dark mode.", {
          id: "color-theme",
          description: "Turn off Follow device to keep Dark all the time.",
        });
        return;
      }
      if (t.key === lightTheme) return;
      setLightTheme(t.key); // instant
      setSaving(t.key);
      await persist(
        { light_theme: t.key },
        `Daytime theme set to ${t.name}`,
        systemDark ? "You'll see it as soon as your device switches to light mode." : undefined
      );
      setSaving(null);
      onPicked?.(t.key);
      return;
    }
    if (t.key === manualTheme && mode === "manual") return;
    setTheme(t.key); // instant, every page
    setSaving(t.key);
    await persist({ color_theme: t.key, theme_mode: "manual" }, `Theme set to ${t.name}`);
    setSaving(null);
    onPicked?.(t.key);
  };

  const toggleAuto = async (on) => {
    if (saving) return;
    const nextMode = on ? "auto" : "manual";
    setMode(nextMode); // instant
    setSaving("mode");
    if (on) {
      await persist(
        { theme_mode: "auto", light_theme: lightTheme },
        "Following your device",
        `Dark in dark mode, ${themeMeta(lightTheme).name} in light mode — saved to your account.`
      );
    } else {
      await persist({ theme_mode: "manual", color_theme: manualTheme }, `Theme set to ${themeMeta(manualTheme).name}`);
    }
    setSaving(null);
  };

  return (
    <div className="space-y-3" data-testid="color-theme-picker" data-mode={mode}>
      <FollowDeviceRow
        isAuto={isAuto}
        systemDark={systemDark}
        lightTheme={lightTheme}
        saving={saving === "mode"}
        onToggle={toggleAuto}
      />

      {isAuto && (
        <p className="px-0.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground" data-testid="theme-daytime-label">
          Daytime theme
        </p>
      )}

      <div
        className={cn("grid gap-3", compact ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5")}
        role="radiogroup"
        aria-label={isAuto ? "Daytime colour theme" : "Colour theme"}
      >
        {themes.map((t) => {
          const isDarkCard = t.tone !== "light";
          const selected = isAuto ? (isDarkCard ? false : lightTheme === t.key) : manualTheme === t.key;
          const autoNight = isAuto && isDarkCard;
          const activeNow = theme === t.key; // what's on screen right now
          return (
            <button
              key={t.key}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-disabled={autoNight || undefined}
              onClick={() => pick(t)}
              disabled={saving !== null}
              data-testid={`color-theme-option-${t.key}`}
              data-selected={selected || undefined}
              data-auto-night={autoNight || undefined}
              data-active-now={activeNow || undefined}
              className={cn(
                "group overflow-hidden rounded-xl border bg-card text-left",
                "transition-[transform,box-shadow,border-color,opacity] duration-150 hover:-translate-y-0.5 hover:shadow-md",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                "disabled:cursor-wait disabled:hover:translate-y-0",
                selected
                  ? "border-primary ring-1 ring-primary"
                  : autoNight
                    ? "border-dashed border-primary/40 hover:translate-y-0 hover:shadow-none"
                    : "border-border hover:border-muted-foreground/40"
              )}
            >
              <ThemeSwatchPreview swatch={t.swatch} tone={t.tone} />
              <div className="space-y-2 border-t border-border/60 px-3.5 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                      {t.name}
                      <Badge variant="outline" className="rounded-full border-border px-1.5 py-0 text-[10px] font-medium text-muted-foreground">
                        {autoNight ? "Night · auto" : t.tagline}
                      </Badge>
                      {isAuto && activeNow && (
                        <Badge
                          className="rounded-full bg-primary px-1.5 py-0 text-[10px] font-semibold text-primary-foreground hover:bg-primary"
                          data-testid={`color-theme-active-now-${t.key}`}
                        >
                          On now
                        </Badge>
                      )}
                    </p>
                    {!compact && (
                      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                        {autoNight ? "Used automatically whenever your device switches to dark mode." : t.description}
                      </p>
                    )}
                  </div>
                  {saving === t.key ? (
                    <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" />
                  ) : selected ? (
                    <span
                      className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
                      data-testid={`color-theme-selected-${t.key}`}
                    >
                      <Check className="h-3 w-3" />
                    </span>
                  ) : autoNight ? (
                    <span
                      className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-accent text-primary"
                      data-testid="color-theme-auto-night-dark"
                    >
                      <Moon className="h-3 w-3" />
                    </span>
                  ) : null}
                </div>
                <SwatchDots swatch={t.swatch} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Modal version for the profile menu (desktop rail, mobile sheet, Field View). */
export const ThemePickerDialog = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-background/95 backdrop-blur-xl" data-testid="theme-settings-dialog" data-ro-allow="">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Palette className="h-4 w-4 text-primary" /> Theme settings
          </DialogTitle>
          <DialogDescription>
            Pick the look that's easiest on your eyes — it applies instantly to every page
            {user ? " and is saved to your account, so it follows you across devices." : "."}
          </DialogDescription>
        </DialogHeader>
        <ThemePicker compact />
        <div className="flex items-center justify-between gap-3 pt-1 text-xs text-muted-foreground">
          <span>Light themes deepen accent colours and bolden numbers so dollar amounts stay readable in daylight.</span>
          {user && user.role !== "contractor" && user.role !== "client" && (
            <Button asChild variant="ghost" size="sm" className="h-7 shrink-0 text-xs" onClick={() => onOpenChange(false)}>
              <Link to="/settings?tab=appearance" data-testid="theme-settings-more-link">
                <SlidersHorizontal className="mr-1 h-3 w-3" /> Background styles
              </Link>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
